<template>
  <el-dialog
    :visible.sync="dialogVisible"
    width="800px"
    :show-close="false"
    append-to-body
  >
    <img
      class="close-img"
      alt="close-icon.png"
      @click="dialogVisible = false"
      src="@/assets/img/images/icon/close-icon.png"
    />
    <p class="dialog-title">
      {{ $t("exchangeRecord.detail_title", { coin: coin }) }}
    </p>
    <div class="detail-content" v-loading="loading">
      <template v-if="detailData">
        <div class="detail-header">
          <div class="order-info">
            <span class="order-label">{{ $t("exchangeRecord.order_no") }}</span>
            <span class="order-value">{{ detailData.orderNo }}</span>
          </div>
          <div :class="['status-tag', getStatusClass(detailData.status)]">
            <span class="status-text">{{ getStatusText(detailData.status) }}</span>
          </div>
        </div>
        <div class="detail-body">
          <div class="info-row">
            <span class="info-label">{{ $t("exchangeRecord.transfer_amount") }}</span>
            <span class="info-value">{{ formatNumber(detailData.transferAmount) }} {{ detailData.exchangeBase }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ $t("exchangeRecord.transfer_account_info") }}</span>
            <span class="info-value">{{ detailData.transferAccountInfo || "-" }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ $t("exchangeRecord.transfer_serial") }}</span>
            <span class="info-value">{{ detailData.transactionNo || "-" }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ $t("exchangeRecord.transfer_time") }}</span>
            <span class="info-value">{{ detailData.transferTime || "-" }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ $t("exchangeRecord.review_time") }}</span>
            <span class="info-value">{{ detailData.reviewTime || "-" }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ $t("exchangeRecord.ht_amount") }}</span>
            <span class="info-value">{{ formatNumber(detailData.htAmount) }} {{ detailData.exchangeCoin }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ $t("exchangeRecord.rate") }}</span>
            <span class="info-value">1 {{ detailData.exchangeBase }} = {{ detailData.rate }} {{ detailData.exchangeCoin }}</span>
          </div>
          <div class="info-row" v-if="detailData.auditAmount != null">
            <span class="info-label">{{ $t("exchangeRecord.actual_transfer") }}</span>
            <span class="info-value highlight">{{ formatNumber(detailData.auditAmount) }} {{ detailData.exchangeBase }}</span>
          </div>
          <div class="info-row" v-if="detailData.rechargeAmount != null">
            <span class="info-label">{{ $t("exchangeRecord.actual_exchange") }}</span>
            <span class="info-value highlight">{{ formatNumber(detailData.rechargeAmount) }} {{ detailData.exchangeCoin }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ $t("exchangeRecord.airdrop_time") }}</span>
            <span class="info-value">{{ detailData.airdropTime || "-" }}</span>
          </div>
          <div
            class="info-row"
            v-if="detailData.status === 'rejected' && detailData.rejectReason"
          >
            <span class="info-label">{{ $t("exchangeRecord.reject_reason") }}</span>
            <span class="info-value reject-reason">{{ detailData.rejectReason }}</span>
          </div>
        </div>
      </template>
    </div>
  </el-dialog>
</template>

<script>
import { mapState } from "vuex";
export default {
  computed: {
    ...mapState(["errorCode"]),
  },
  data() {
    return {
      dialogVisible: false,
      loading: false,
      id: "",
      coin: "",
      detailData: null,
    };
  },
  methods: {
    initDialog(id, coin) {
      this.id = id || "";
      this.coin = coin || "";
      this.dialogVisible = true;
      this.detailData = null;
      this.loadDetail();
    },
    loadDetail() {
      this.loading = true;
      this.$axios
        .post("/userAssets/tranRecordDetail", {
          queryType: "deposit",
          id: this.id,
        })
        .then((res) => {
          if (
            res.data.code === this.errorCode.ok &&
            res.data.data &&
            res.data.data.deposit
          ) {
            this.detailData = this.mapDetail(res.data.data.deposit);
            if (!this.coin && this.detailData.exchangeCoin) {
              this.coin = this.detailData.exchangeCoin;
            }
          }
        })
        .catch(() => {
          this.detailData = null;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    parseMemo(memo) {
      if (!memo) return {};
      if (typeof memo === "object") return memo;
      try {
        return JSON.parse(memo);
      } catch (e) {
        const result = {};
        String(memo)
          .split(/[&;\n]/)
          .forEach((pair) => {
            const idx = pair.indexOf("=");
            if (idx > -1) {
              const key = pair.slice(0, idx).trim();
              const val = pair.slice(idx + 1).trim();
              if (key) result[key] = val;
            }
          });
        return result;
      }
    },
    formatTime(time) {
      if (!time) return "";
      return this.dateFormatter(new Date(time), "yyyy-MM-dd hh:mm:ss");
    },
    mapDetail(item) {
      const memoObj = this.parseMemo(item.memo);
      const exchangeInfo = memoObj.exchangeInfo || {};
      const transferInfo = memoObj.transferInfo || {};
      const transferName =
        item.transferName || transferInfo.transferName || memoObj.transferName;
      const transferAccount =
        item.fromAddress || transferInfo.transferAccount;
      const accountParts = [transferName, transferAccount].filter(Boolean);
      const auditTime = item.auditTime || item.confirmTime || item.updateTime;
      return {
        orderNo: item.txHash || "",
        status: this.mapStatus(item.status, item.statusStr),
        statusStr: item.statusStr || "",
        transferAmount: exchangeInfo.transferAmount,
        exchangeBase: exchangeInfo.exchangeBase || "",
        exchangeCoin: exchangeInfo.exchangeCoin || "",
        rechargeAmount: memoObj.rechargeAmount,
        auditAmount: memoObj.auditAmount,
        rate: exchangeInfo.exchangeRate,
        transferAccountInfo: accountParts.join(" ") || "-",
        transactionNo: item.txHash || "-",
        transferTime: this.formatTime(item.createTime),
        reviewTime: this.formatTime(auditTime),
        htAmount: exchangeInfo.exchangeAmount,
        airdropTime: this.formatTime(auditTime),
        rejectReason: item.remark || item.rejectReason || "",
      };
    },
    mapStatus(status, statusStr) {
      const str = statusStr || "";
      if (str.indexOf("拒") > -1 || str.toLowerCase().indexOf("reject") > -1) {
        return "rejected";
      }
      if (
        str.indexOf("完成") > -1 ||
        str.indexOf("成功") > -1 ||
        str.toLowerCase().indexOf("complet") > -1 ||
        str.toLowerCase().indexOf("success") > -1
      ) {
        return "completed";
      }
      if (str.indexOf("审核") > -1 || str.toLowerCase().indexOf("pend") > -1) {
        return "pending";
      }
      switch (Number(status)) {
        case 1:
          return "completed";
        case 2:
          return "rejected";
        default:
          return "pending";
      }
    },
    getStatusClass(status) {
      const classMap = {
        completed: "status-completed",
        pending: "status-pending",
        rejected: "status-rejected",
      };
      return classMap[status] || "status-pending";
    },
    getStatusText(status) {
      const textMap = {
        completed: this.$t("exchangeRecord.status_completed"),
        pending: this.$t("exchangeRecord.status_pending"),
        rejected: this.$t("exchangeRecord.status_rejected"),
      };
      return textMap[status] || status;
    },
    formatNumber(num) {
      if (!num && num !== 0) return "0.00";
      return Number(num).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style lang="less" scoped>
/deep/.el-dialog {
  background: #ffffff;
  border-radius: 10px;
  .el-dialog__body {
    padding-top: 0px;
    position: relative;
  }
}

.close-img {
  top: -10px;
  right: 20px;
  width: 12px;
  height: 12px;
  cursor: pointer;
  position: absolute;
  z-index: 10;
}

.dialog-title {
  font-family: PingFang SC, PingFang SC;
  font-weight: 800;
  font-size: 24px;
  color: #333333;
  margin-bottom: 20px;
}

.detail-content {
  min-height: 200px;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: #f8f9ff;
  border-radius: 12px;
  margin-bottom: 16px;

  .order-info {
    display: flex;
    align-items: center;

    .order-label {
      font-size: 14px;
      color: #999999;
      margin-right: 8px;
    }

    .order-value {
      font-size: 15px;
      font-weight: 600;
      color: #333333;
    }
  }

  .status-tag {
    padding: 4px 12px;
    border-radius: 20px;

    &.status-completed .status-text {
      color: #52c41a;
    }

    &.status-pending .status-text {
      color: var(--primary-color);
    }

    &.status-rejected .status-text {
      color: #ff4d4f;
    }

    .status-text {
      font-size: 13px;
      font-weight: 500;
    }
  }
}

.detail-body {
  padding: 0 24px;

  .info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    border-bottom: 1px solid #f5f5f5;

    &:last-child {
      border-bottom: none;
    }

    .info-label {
      font-size: 14px;
      color: #999999;
    }

    .info-value {
      font-size: 14px;
      color: #333333;
      text-align: right;
      max-width: 60%;

      &.highlight {
        color: var(--primary-color);
        font-weight: 600;
      }

      &.reject-reason {
        color: #ff4d4f;
      }
    }
  }
}
</style>
